import { Reducer } from 'redux';
import _ from 'lodash';
import { IReduxAction } from 'app/types/actions';
import splitTypes from 'app/utils/static/splitTypes';
import collectCategories from 'app/utils/static/collectCategories';
import { TeamPaymentRequestsActionTypes as types, ITeamPaymentRequestsState } from './types';

const initialState: ITeamPaymentRequestsState = {
  teamPaymentRequestsById: {},
  teamPaymentRequestIds: [],
  currentTeamPaymentRequestId: undefined,

  isLoading: false,
  isSaving: false,
  hasLoaded: false,
  hasSaved: false,
  hasFailed: false,
  requestedData: {},
  backendErrors: {},
  showAddPayment: false,
  filterConfig: {},
  sortConfig: {},
  pagination: {
    page: {
      size: 200,
      number: 0,
    },
    filter: [],
    sort: [],
    meta: {
      totalCount: undefined,
      totalPages: undefined,
    },
  },
  formData: {
    paymentType: collectCategories[0],
    splitType: splitTypes[0],
    description: undefined,
    amount: undefined,
    dueDate: undefined,
  },
  originalFormData: {
    paymentType: collectCategories[0],
    splitType: splitTypes[0],
    description: undefined,
    amount: undefined,
    dueDate: undefined,
  },
  payeeType: 'individuals',
  selectedRows: {},
  selectedAllRows: false,
  selectedRowIds: [],
  selectedRowsById: {},
  errors: {},
  changed: false,
  saved: false,
  initialFilter: undefined,
};

const reducer: Reducer<ITeamPaymentRequestsState> = (
  state = initialState,
  action: IReduxAction,
) => {
  switch (action.type) {
    case types.GET_ALL_DASHBOARD_TEAM_PAYMENT_REQUESTS_PENDING: {
      return {
        ...state,
        isLoading: true,
        hasLoaded: false,
        pagination: {
          ...state.pagination,
          ...action.payload.pagination,
        },
      };
    }
    case types.GET_ALL_DASHBOARD_TEAM_PAYMENT_REQUESTS_SUCCESS: {
      return {
        ...state,
        teamPaymentRequestsById: action.payload.teamPaymentRequestsById,
        teamPaymentRequestIds: action.payload.teamPaymentRequestIds,
        isLoading: false,
        hasLoaded: true,
        pagination: {
          ...state.pagination,
          meta: action.payload.meta,
        },
      };
    }
    case types.GET_ALL_DASHBOARD_TEAM_PAYMENT_REQUESTS_FAIL: {
      return {
        ...state,
        isLoading: false,
        hasLoaded: false,
        backendErrors: action.payload.backendErrors,
      };
    }
    case types.GET_DASHBOARD_TEAM_PAYMENT_REQUEST_PENDING: {
      return {
        ...state,
        isLoading: true,
        hasLoaded: false,
      };
    }
    case types.GET_DASHBOARD_TEAM_PAYMENT_REQUEST_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        hasLoaded: true,
        teamPaymentRequestsById: _.merge(
          {},
          state.teamPaymentRequestsById,
          action.payload.teamPaymentRequestsById,
        ),
        teamPaymentRequestIds: _.union(state.teamPaymentRequestIds, [
          action.payload.currentTeamPaymentRequestId,
        ]),
        currentTeamPaymentRequestId: action.payload.currentTeamPaymentRequestId,
      };
    }
    case types.GET_DASHBOARD_TEAM_PAYMENT_REQUEST_FAIL: {
      return {
        ...state,
        isLoading: false,
        hasLoaded: false,
        backendErrors: action.payload.backendErrors,
      };
    }
    case types.SELECT_DASHBOARD_TEAM_PAYMENT_REQUEST_REQUESTED: {
      return {
        ...state,
        currentTeamPaymentRequestId: action.payload.currentTeamPaymentRequestId,
      };
    }
    case types.UPDATE_DASHBOARD_TEAM_PAYMENT_REQUEST_PENDING: {
      return {
        ...state,
        isSaving: true,
        hasSaved: false,
        hasFailed: false,
        requestedData: action.payload,
        backendErrors: {},
      };
    }
    case types.UPDATE_DASHBOARD_TEAM_PAYMENT_REQUEST_SUCCESS: {
      return {
        ...state,
        isSaving: false,
        hasSaved: true,
        hasFailed: false,
        teamPaymentRequestsById: _.merge(
          {},
          state.teamPaymentRequestsById,
          action.payload.teamPaymentRequestsById,
        ),
        teamPaymentRequestIds: _.union(state.teamPaymentRequestIds, [
          action.payload.currentTeamPaymentRequestId,
        ]),
        currentTeamPaymentRequestId: action.payload.currentTeamPaymentRequestId,
        backendErrors: {},
      };
    }
    case types.UPDATE_DASHBOARD_TEAM_PAYMENT_REQUEST_FAIL: {
      return {
        ...state,
        isSaving: false,
        hasFailed: true,
        hasSaved: false,
        backendErrors: action.payload.backendErrors,
      };
    }
    case types.CREATE_DASHBOARD_TEAM_PAYMENT_REQUEST_PENDING: {
      return {
        ...state,
        isSaving: true,
        hasSaved: false,
        hasFailed: false,
        requestedData: action.payload,
        backendErrors: {},
      };
    }
    case types.CREATE_DASHBOARD_TEAM_PAYMENT_REQUEST_SUCCESS: {
      return {
        ...state,
        isSaving: false,
        hasSaved: true,
        hasFailed: false,
        teamPaymentRequestsById: _.merge(
          {},
          state.teamPaymentRequestsById,
          action.payload.teamPaymentRequestsById,
        ),
        teamPaymentRequestIds: _.union(state.teamPaymentRequestIds, [
          action.payload.currentTeamPaymentRequestId,
        ]),
        currentTeamPaymentRequestId: action.payload.currentTeamPaymentRequestId,
        backendErrors: {},
      };
    }
    case types.CREATE_DASHBOARD_TEAM_PAYMENT_REQUEST_FAIL: {
      return {
        ...state,
        isSaving: false,
        hasFailed: true,
        hasSaved: false,
        backendErrors: action.payload.backendErrors,
      };
    }
    case types.DELETE_DASHBOARD_TEAM_PAYMENT_REQUEST_PENDING: {
      return {
        ...state,
        isSaving: true,
        hasSaved: false,
        hasFailed: false,
      };
    }
    case types.DELETE_DASHBOARD_TEAM_PAYMENT_REQUEST_SUCCESS: {
      return {
        ...state,
        isSaving: false,
        hasSaved: true,
        hasFailed: false,
        teamPaymentRequestsById: _.omit(
          state.teamPaymentRequestsById,
          action.payload.currentTeamPaymentRequestId,
        ),
        teamPaymentRequestIds: _.without(
          state.teamPaymentRequestIds,
          action.payload.currentTeamPaymentRequestId,
        ),
        currentTeamPaymentRequestId: undefined,
        backendErrors: {},
      };
    }
    case types.DELETE_DASHBOARD_TEAM_PAYMENT_REQUEST_FAIL: {
      return {
        ...state,
        isSaving: false,
        hasFailed: true,
        hasSaved: false,
        backendErrors: action.payload.backendErrors,
      };
    }

    case types.CLEAR_DASHBOARD_PAYMENT_REQUEST: {
      return initialState;
    }
    case types.UPDATE_DASHBOARD_PAYMENT_REQUEST: {
      return {
        ...state,
        ...action.payload,
      };
    }
    default:
      return state;
  }
};

export default reducer;
